import React from "react";
import { withTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTire, faBriefcase, faTruck, faCar } from "@fortawesome/pro-solid-svg-icons";
import { faList } from "@fortawesome/pro-solid-svg-icons";
import moment from "moment/moment";

import { CHECKLIST_TYPES } from "../../../../util/common";
import SectionCard from "../SectionCard";
import SubSection from "../SubSection";
import Legend from "./components/Legend";
import QuestionItem from "./components/QuestionItem";
import TireQuestionItem from "./components/TireQuestionItem";
import { isOnCarTyre, isInStorageTyre, isOnTruckTyre, groupTruckTyreByAxle } from "./util";

import "./index.scss";

const renderQuestionItems = (group, questionItems) => {
  const nonTyreQuestions = questionItems?.filter(questionItem => !questionItem.tyre_id);

  if (!nonTyreQuestions.length) return null;

  return (
    <SubSection title={group}>
      {nonTyreQuestions.map((question, idx) => (
        <QuestionItem key={idx} question={question} />
      ))}
    </SubSection>
  );
};

const renderOnCarTyreQuestionItems = (questionItems, settings, t) => {
  const onCarTyres = questionItems.filter(questionItem => questionItem.tyre && isOnCarTyre(questionItem.tyre_position));

  if (!onCarTyres?.length) return null;

  return (
    <SubSection title={t("cc_ON_CAR").message || "ON CAR"} icon={<FontAwesomeIcon icon={faCar} />}>
      {onCarTyres.map((question, idx) => (
        <TireQuestionItem key={idx} settings={settings} question={question} hideTyrePositionLabels />
      ))}
    </SubSection>
  );
};

const renderInStorageTyreQuestionItems = (questionItems, settings, t) => {
  const inStorageTyres = questionItems.filter(questionItem => questionItem.tyre && isInStorageTyre(questionItem.tyre_position));

  if (!inStorageTyres?.length) return null;

  return (
    <SubSection title={t("cc_IN_STORAGE").message || "IN STORAGE"} icon={<FontAwesomeIcon icon={faCar} />}>
      {inStorageTyres.map((question, idx) => (
        <TireQuestionItem key={idx} settings={settings} question={question} hideTyrePositionLabels />
      ))}
    </SubSection>
  );
};

const renderTruckTyreQuestionItems = (questionItems, settings, t) => {
  const truckTyres = questionItems.filter(questionItem => questionItem.tyre && isOnTruckTyre(questionItem.tyre_position));
  const truckTyreAxleMapList = groupTruckTyreByAxle(truckTyres, t);

  if (!truckTyreAxleMapList?.length) return null;

  return truckTyreAxleMapList.map(([key, values]) => (
    <SubSection key={key} title={t("cc_ON_TRUCK").message || "ON TRUCK"} subTitle={key} icon={<FontAwesomeIcon icon={faTruck} />}>
      {values.map((question, idx) => (
        <TireQuestionItem key={idx} settings={settings} question={question} />
      ))}
    </SubSection>
  ));
};

const renderGroup = (group, questionItems, settings, t) => (
  <>
    {renderQuestionItems(group, questionItems)}
    {renderOnCarTyreQuestionItems(questionItems, settings, t)}
    {renderInStorageTyreQuestionItems(questionItems, settings, t)}
    {renderTruckTyreQuestionItems(questionItems, settings, t)}
  </>
);

const renderGroups = (groupedQuestionResultsArray, settings, t) =>
  groupedQuestionResultsArray.map(([group, questionItems]) => {
    return <React.Fragment key={group}>{renderGroup(group, questionItems, settings, t)}</React.Fragment>;
  });

const renderCheckHeaderTitle = (check, t) => {
  const title = check.checklist?.checklist_type === CHECKLIST_TYPES.ExtraParts ? t("cc_extra_parts", "Extra parts") : check.checklist?.name;
  const icon = check._isTyreCheck ? faTire : check.checklist?.checklist_type === CHECKLIST_TYPES.ExtraParts ? faBriefcase : faList;

  return (
    <div
      className="multi-check-title-element"
      style={{
        backgroundColor: check.checklist?.template?.primary_color || "",
        color: check.checklist?.template?.text_color || "",
        borderColor: check.checklist?.template?.text_color || "",
      }}
    >
      <FontAwesomeIcon icon={icon} />
      <span className="check-title-primary">{`#${check._order} ${title} ${check.purpose ? ` - ${check.purpose}` : ""}`}</span>
      {!check.checklist?.template?.hide_timestamp && check.mechanic && (
        <span className="check-title-meta">
          <span className="check-title-meta-part">{`${check.mechanic.first_name || check.mechanic.last_name ? " - " : ""} ${check.mechanic.first_name} ${
            check.mechanic.last_name
          }`}</span>
          <span className="check-title-meta-separator">-</span>
          <span className="check-title-meta-part check-title-meta-part--date">
            {`${moment(check.created_on)
              .add(check.duration, "seconds")
              .format("HH:mm DD-MM-YYYY")}`}
            <Icon name="arrow right" />
          </span>
          <span className="check-title-meta-part">{`${moment(check.created_on).format("DD-MM-YYYY")}`}</span>
        </span>
      )}
    </div>
  );
};

const Checks = ({ check, settings, t }) => {
  const groupedQuestionResultsArray = Array.from(check._groupedQuestionResults);

  return (
    <div className="checks-section">
      <SectionCard titleElement={renderCheckHeaderTitle(check, t)} legend={<Legend />}>
        {renderGroups(groupedQuestionResultsArray, settings, t)}
      </SectionCard>
    </div>
  );
};

export default withTranslation()(Checks);
